import { useMemo } from 'react';
import { cn } from '@/lib/cn';

/**
 * AI 기본 분석 결과(`# 섹션` 형식 텍스트) 를 구조화 카드 UI 로 렌더.
 *
 * 입력 포맷 (BE 프롬프트 출력 예시):
 *   # 어떤 회사인가요?
 *   종목명과 주요 사업 ...
 *
 *   # 지금 비싼가요?
 *   PER 12.3배 → [고평가] 업종 평균과 비교 ...
 *   PBR 2.1배 → [높음] ...
 *   EPS 1500원 → [높음] ...
 *
 *   # 투자 시 주의할 점
 *   이 종목의 주요 리스크 ...
 *
 *   # 주요이력  (이력 데이터 있을 때만)
 *   - 배당: 5회 (최근 2025년)
 *   - 사명변경: 2회 (최근 2018년)
 *
 *   주요이력코멘트: 사명변경이 잦아 ...
 *
 *   # 한줄 요약
 *   ...
 *
 *   본 분석은 투자 권유가 아닌 ...
 */

/* ──────────────────────────────────────────────────────────────
 * 파서
 * ────────────────────────────────────────────────────────────── */

interface ParsedSection {
  title: string;
  body: string;
}

interface MetricRow {
  /** 라벨 (예: "PER", "매출 성장", "순이익") */
  label: string;
  /** 수치 (예: "12.3배", "+5%"). 라벨에 수치가 없으면 빈 문자열. */
  value: string;
  /** 대괄호 안 판단 (예: "고평가", "성장"). 없으면 null. */
  badge: string | null;
  /** 판단 뒤 설명 텍스트 */
  description: string;
}

interface HistoryItem {
  /** 한국어 이벤트 이름 (예: "유상증자", "사명변경", "배당") */
  label: string;
  /** "5회" 또는 "5회 (최근 2018년)" 등 원본 우측 텍스트 */
  meta: string;
}

function parseSections(text: string): ParsedSection[] {
  const lines = text.split('\n');
  const out: ParsedSection[] = [];
  let cur: ParsedSection | null = null;
  for (const raw of lines) {
    const m = raw.match(/^#\s+(.+)$/);
    if (m) {
      if (cur) out.push({ ...cur, body: cur.body.trim() });
      cur = { title: m[1].trim(), body: '' };
    } else if (cur) {
      cur.body += (cur.body ? '\n' : '') + raw;
    }
  }
  if (cur) out.push({ ...cur, body: cur.body.trim() });
  return out;
}

/** 본문에서 "면책 문구" 로 끝나는 마지막 단락은 분리 (한줄 요약 카드 하단 미니 텍스트로 노출) */
function extractDisclaimer(text: string): { body: string; disclaimer: string | null } {
  const idx = text.indexOf('본 분석은 투자 권유가 아닌');
  if (idx < 0) return { body: text, disclaimer: null };
  return {
    body: text.slice(0, idx).trim(),
    disclaimer: text.slice(idx).trim(),
  };
}

/** "PER 12.3배 → [고평가] 설명" → MetricRow */
function parseMetricRow(line: string): MetricRow | null {
  const m = line.match(/^(.+?)\s*→\s*\[([^\]]+)\]\s*(.*)$/);
  if (!m) return null;
  const labelValueRaw = m[1].trim();
  const badge = m[2].trim();
  const description = m[3].trim();
  // 라벨에서 수치 분리: 마지막 토큰이 숫자/특수문자로 시작하면 수치로 간주
  const lv = labelValueRaw.match(/^(.*?)\s+([-+\d.][^\s]*)$/);
  if (lv) {
    return { label: lv[1].trim(), value: lv[2].trim(), badge, description };
  }
  return { label: labelValueRaw, value: '', badge, description };
}

/** "- 배당: 5회 (최근 2025년)" → HistoryItem */
function parseHistoryItem(line: string): HistoryItem | null {
  const m = line.match(/^[-•]\s*([^:]+):\s*(.+)$/);
  if (!m) return null;
  return { label: m[1].trim(), meta: m[2].trim() };
}

/* ──────────────────────────────────────────────────────────────
 * 색상 매핑
 * ────────────────────────────────────────────────────────────── */

type Tone = 'pos' | 'neg' | 'neu';

const POSITIVE_BADGES = new Set([
  '저평가', '적정', '적당', '우수', '양호', '안전', '매력적', '성장', '증가', '매우높음',
]);
const NEGATIVE_BADGES = new Set([
  '고평가', '낮음', '위험', '주의', '감소', '없음',
]);

function badgeTone(badge: string): Tone {
  if (POSITIVE_BADGES.has(badge)) return 'pos';
  if (NEGATIVE_BADGES.has(badge)) return 'neg';
  return 'neu';
}

const BADGE_TONE_CLS: Record<Tone, string> = {
  pos: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  neg: 'bg-rose-50 text-rose-700 border-rose-200',
  neu: 'bg-slate-100 text-slate-600 border-slate-200',
};

/** 주요이력 항목별 색상 — 빨강/주황/초록 분기 */
const HISTORY_RED = new Set(['유상증자', '감자']);
const HISTORY_ORANGE = new Set(['사명변경', '전환사채(CB)', '전환사채', 'CB', '신주인수권부사채(BW)', '신주인수권부사채', 'BW']);
const HISTORY_GREEN = new Set(['배당', '자사주 소각', '소각', '무상증자']);
function historyTone(label: string): 'red' | 'orange' | 'green' | 'gray' {
  if (HISTORY_RED.has(label)) return 'red';
  if (HISTORY_ORANGE.has(label)) return 'orange';
  if (HISTORY_GREEN.has(label)) return 'green';
  return 'gray';
}
const HISTORY_CARD_CLS: Record<'red' | 'orange' | 'green' | 'gray', string> = {
  red: 'border-rose-200 bg-rose-50 text-rose-800',
  orange: 'border-amber-200 bg-amber-50 text-amber-800',
  green: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  gray: 'border-slate-200 bg-slate-50 text-slate-700',
};

/* ──────────────────────────────────────────────────────────────
 * 서브 컴포넌트
 * ────────────────────────────────────────────────────────────── */

function MetricCard({ row }: { row: MetricRow }) {
  const tone = row.badge ? badgeTone(row.badge) : 'neu';
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-white p-4">
      <div className="mb-1 text-xs font-medium text-[var(--color-text-sub)]">{row.label}</div>
      {row.value && (
        <div className="mb-2 text-xl font-bold tabular-nums text-[var(--color-text)]">
          {row.value}
        </div>
      )}
      {row.badge && (
        <span
          className={cn(
            'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
            BADGE_TONE_CLS[tone],
          )}
        >
          {row.badge}
        </span>
      )}
      {row.description && (
        <p className="mt-2 text-xs leading-relaxed text-[var(--color-text-sub)]">
          {row.description}
        </p>
      )}
    </div>
  );
}

function SectionCard({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'rounded-2xl border border-[var(--color-border)] bg-white p-4 sm:p-5',
        className,
      )}
    >
      <h3 className="mb-3 text-base font-bold text-[var(--color-text)]">{title}</h3>
      {children}
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────
 * 메인 컴포넌트
 * ────────────────────────────────────────────────────────────── */

export function AiAnalysisRenderer({ analysis }: { analysis: string }) {
  // 본문에서 면책 문구는 떼어내기만 함 — 실제 노출은 호출 측(TickerPage 등)에서 단일 출처로 처리
  const sections = useMemo(() => {
    const { body } = extractDisclaimer(analysis);
    return parseSections(body);
  }, [analysis]);

  return (
    <div className="space-y-3">
      {sections.map((sec, idx) => (
        <SectionRenderer key={`${sec.title}-${idx}`} section={sec} />
      ))}
    </div>
  );
}

function SectionRenderer({ section }: { section: ParsedSection }) {
  const { title, body } = section;
  const lines = body.split('\n').map((l) => l.trim()).filter(Boolean);

  // 주의/리스크 섹션 — 빨강 카드
  if (title.includes('주의') || title.includes('리스크')) {
    /*
     * AI가 "첫째, ... 둘째, ... 셋째, ..." 를 한 단락에 붙여 출력하는 경우가 잦아
     * 서수 표현 앞에서 강제 개행하여 가독성 확보.
     */
    const formatted = body.replace(
      /(\S)\s*(첫째|둘째|셋째|넷째|다섯째|여섯째|일곱째|여덟째|아홉째|열째)([,.])/g,
      '$1\n\n$2$3',
    );
    return (
      <SectionCard
        title={title}
        className="border-rose-200 bg-rose-50/50"
      >
        <div className="whitespace-pre-wrap text-sm leading-relaxed text-rose-900">
          {formatted}
        </div>
      </SectionCard>
    );
  }

  // 한줄 요약 — primary 강조 카드
  if (title.includes('한줄 요약') || title.includes('한 줄 요약')) {
    return (
      <SectionCard
        title={title}
        className="border-[color-mix(in_srgb,var(--color-primary)_25%,transparent)] bg-[color-mix(in_srgb,var(--color-primary)_5%,transparent)]"
      >
        <p className="text-sm font-medium leading-relaxed text-[var(--color-text)]">{body}</p>
      </SectionCard>
    );
  }

  // 주요이력 — 색상별 항목 + 코멘트
  if (title.includes('주요이력') || title.includes('주요 이력')) {
    const items: HistoryItem[] = [];
    let comment = '';
    for (const line of lines) {
      if (line.startsWith('주요이력코멘트:') || line.startsWith('주요이력 코멘트:')) {
        comment = line.replace(/^주요이력\s*코멘트:\s*/, '').trim();
        continue;
      }
      const item = parseHistoryItem(line);
      if (item) items.push(item);
    }
    return (
      <SectionCard title={title}>
        <div className="space-y-2">
          {items.map((it, i) => {
            const tone = historyTone(it.label);
            return (
              <div
                key={i}
                className={cn(
                  'flex items-baseline justify-between gap-2 rounded-lg border px-3 py-2 text-sm',
                  HISTORY_CARD_CLS[tone],
                )}
              >
                <span className="font-semibold">{it.label}</span>
                <span className="text-xs">{it.meta}</span>
              </div>
            );
          })}
          {items.length === 0 && (
            <div className="rounded-lg border border-dashed border-[var(--color-border)] p-3 text-xs text-[var(--color-text-sub)]">
              이력 데이터가 없어요.
            </div>
          )}
        </div>
        {comment && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm leading-relaxed text-amber-900">
            {comment}
          </div>
        )}
      </SectionCard>
    );
  }

  // 메트릭 카드 후보 — "라벨 → [판단] 설명" 패턴이 다수면 그리드 카드
  const metrics = lines.map(parseMetricRow).filter((r): r is MetricRow => !!r);

  if (metrics.length >= 1 && metrics.length === lines.length) {
    /* 모든 라인이 메트릭 패턴이면 카드 그리드.
       1개일 땐 단일 컬럼, 2~3개는 그리드. */
    const cols =
      metrics.length === 1
        ? 'grid-cols-1'
        : metrics.length === 2
        ? 'grid-cols-1 sm:grid-cols-2'
        : 'grid-cols-1 sm:grid-cols-3';
    return (
      <SectionCard title={title} className="bg-[var(--color-bg)]">
        <div className={cn('grid gap-3', cols)}>
          {metrics.map((r, i) => (
            <MetricCard key={i} row={r} />
          ))}
        </div>
      </SectionCard>
    );
  }

  // 일반 텍스트 섹션 (어떤 회사인가요?, 등)
  return (
    <SectionCard title={title}>
      <div className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--color-text)]">
        {body}
      </div>
    </SectionCard>
  );
}
